import * as React from "react"
import ReportLoadingMessage from "../ReportLoadingMessage"
import SummaryFilters from "../SummaryFilters"
import { CurrentStatus, getAllStudentsLiveViewInfo, LiveViewStatusFull } from "../../../services/liveview/liveview-service"
import { isDevelopment } from "../../../services/utils/env-helper"
import { TeacherStore, ITeacherStoreData, NotificationType } from "../../../stores/teacher.store"
import { GraduationYearDescriptor, StudentData } from "../types"
import { NOT_SPECIFIED } from "../../../constants"
import { getBlendedStudentsInfo, getStatsForAllQuestionsPerPeriod, IBellringerTestUserAnswer, IQuestionStats } from "../../../services/bellringers"
import { ITimeOnPlatform } from "../TimeOnPlatform/top-lesson-processor"
import BellringersPeriodSubview from "./BellringersPeriodSubview"
import BellringersSubjectSubview from "./BellringersSubjectSubview"
import BellringersModal from "../../Bellringers/BellringerModal"
import BellringerQuestionReviewForTeacher from "./BellringerQuestionReviewForTeacher"
import "./index.css"

export interface Props {}

interface State {
    selectedSchoolName?: string
    selectedGraduationYear?: string
    selectedPeriod?: string
    graduationYears: GraduationYearDescriptor[]
    selectedTimeRange?: string
    searchQuery?: string
    selectedSubject?: string
    data: StudentData[]
    selectedData: StudentData[]
    liveData: LiveViewStatusFull[]
    timeOnPlatformInfo: ITimeOnPlatform[]
    studentsBellringers: IBellringerTestUserAnswer[]
    bellringersMetaDataMap: any
    fullBellringersInfo?: any
    selectedBellringerId?: string
    bellringerQuestionStats?: IQuestionStats
    loaded: boolean
    showModalForBellringerQuestionReview: boolean
}

export default class BellringersOverview extends React.Component<Props, State> {
    unsubscribe

    constructor(props: Props) {
        super(props)
        this.state = {
            selectedSchoolName: undefined,
            selectedGraduationYear: undefined,
            selectedPeriod: undefined,
            selectedSubject: "english",
            data: [],
            selectedData: [],
            graduationYears: [],
            searchQuery: "",
            liveData: [],
            timeOnPlatformInfo: [],
            studentsBellringers: [],
            bellringersMetaDataMap: {},
            loaded: false,
            showModalForBellringerQuestionReview: false,
        }
    }

    public async componentDidMount() {
        TeacherStore.loadStudents()
        TeacherStore.loadStudentsBellringersWithMetaData()
        this.unsubscribe = TeacherStore.subscribe((d, t) => {
            this.setMainData(d, t)
        })
    }

    componentWillUnmount() {
        this.unsubscribe()
    }

    setMainData(data: ITeacherStoreData, t?: NotificationType) {
        if (t !== NotificationType.STUDENTS && t !== NotificationType.STUDENTS_BELLRINGERS_WITH_METADATA && t !== NotificationType.GENERIC) {
            return
        }

        let { students, studentsActivities, graduationYears, studentsBellringersWithMetaData, timeOnPlatformInfo } = data

        // Make sure to initialize the studentsBellringers & bellringersMetaDataMap since studentsBellringersWithMetaData might be empty {}
        let { userBellringers: studentsBellringers=[], bellringersMetaDataMap={} } = studentsBellringersWithMetaData

        this.setState(
            {
                data: students,
                selectedData: students,
                liveData: studentsActivities,
                graduationYears,
                timeOnPlatformInfo,
                studentsBellringers,
                bellringersMetaDataMap,
                loaded: true,
            },
            () => this.applyFilter(),
        )
    }

    onGraduationYearSelected(selectedYear: string) {
        this.setState({ selectedGraduationYear: selectedYear === "All" ? undefined : selectedYear }, () => this.applyFilter())
    }

    onNewSchoolSelected(selectedSchool: string) {
        this.setState(
            {
                selectedSchoolName: selectedSchool === "All" ? undefined : selectedSchool,
            },
            () => this.applyFilter(),
        )
    }

    onNewPeriodSelected(selectedPeriod: string) {
        this.setState(
            {
                selectedPeriod: selectedPeriod === "All" ? undefined : selectedPeriod,
            },
            () => this.applyFilter(),
        )
    }

    onTimeRangeSelected(selected: string) {}

    onSubjectSelected(selected: string) {
        this.setState(
            {
                selectedSubject: selected,
            },
            () => this.applyFilter(),
        )
    }

    private applyFilter = () => {
        let { data, selectedGraduationYear, selectedSchoolName, selectedPeriod } = this.state
        let selectedData = data.filter((d) => {
            let selectStudent = true
            let period = d.period || NOT_SPECIFIED
            if (
                (selectedGraduationYear && selectedGraduationYear !== d.graduationYear) ||
                (selectedSchoolName && selectedSchoolName !== d.schoolName) ||
                (selectedPeriod && selectedPeriod !== period)
            ) {
                selectStudent = false
            }
            return selectStudent
        })

        let {selectedSubject, timeOnPlatformInfo, studentsBellringers} = this.state
        let fullBellringersInfo = this.getFullBellringersInfoWithStats(selectedSubject, selectedPeriod, selectedData, timeOnPlatformInfo, studentsBellringers)

        this.setState({ selectedData, fullBellringersInfo })
    }

    handleSearch = (searchQuery) => {
        let { data } = this.state

        this.setState({ searchQuery })

        searchQuery = searchQuery ? searchQuery.trim().toLowerCase() : ""

        if (!searchQuery) {
            this.setState({ selectedData: data })
            return
        }

        let keys = searchQuery.split(/[ *]/).filter((k) => k)
        let selectedData = this.state.data.filter((d) => {
            let firstName = d.firstName ? d.firstName.toLowerCase() : ""
            let lastName = d.lastName ? d.lastName.toLowerCase() : ""
            let combined = `${firstName} ${lastName}`
            let included = true
            let selectionMap = {}

            keys.forEach((key) => (selectionMap[key] = combined.includes(key) ? true : false))
            Object.keys(selectionMap).forEach((k) => {
                if (!selectionMap[k]) {
                    included = false
                }
            })

            return included
        })
        this.setState({ selectedData })
    }

    clearSearch = () => {
        this.setState({ searchQuery: "" })
        this.handleSearch("")
    }

    getFullBellringersInfoWithStats = (selectedSubject, selectedPeriod, selectedData, timeOnPlatformInfo, studentsBellringers) => {
        let studentsInfo = getBlendedStudentsInfo(selectedData, timeOnPlatformInfo, studentsBellringers)
        let questionsStats = getStatsForAllQuestionsPerPeriod(studentsInfo)

        return {subject: selectedSubject, period: selectedPeriod, studentsInfo, questionsStats}
    }

    handleQuestionClick = (bellringerId: string, period: string) => {
        let {fullBellringersInfo} = this.state
        let {questionsStats} = fullBellringersInfo
        let stats = questionsStats as any[]
        let bellringerQuestionStats = stats.find(d => d.bellringerId === bellringerId && (d.period === period || (period === NOT_SPECIFIED && d.period === "")))

        if (!bellringerQuestionStats) return

        this.setState({
            selectedBellringerId: bellringerId, 
            bellringerQuestionStats,
            showModalForBellringerQuestionReview: true, 
        })
    }

    handleReviewClose = () => {
        this.setState({
            selectedBellringerId: undefined,
            bellringerQuestionStats: undefined,
            showModalForBellringerQuestionReview: false, 
        })
    }

    public render() {
        let { fullBellringersInfo, selectedPeriod="All", selectedSubject="english", loaded, showModalForBellringerQuestionReview, selectedBellringerId, bellringerQuestionStats } = this.state

        if (!fullBellringersInfo) return null

        let bellringersInfo = fullBellringersInfo
        let periodSubviewActive = selectedPeriod && selectedPeriod !== "All"
        let subviewInfo = {
            ...bellringersInfo,
        }
        let teacherReviewInfo = { bellringerQuestionStats }
        let grades = [9, 10, 11, 12] // Corresponding to "Sets" 1, 2, 3, 4
        let allowedSubjects = ["english", "math", "reading"]

        return (
            <div className="lesson-overview">
                <SummaryFilters
                    enableTimeRangeSelection={false}
                    enablePeriodSelection={true}
                    enableSubjectSelection={true}
                    allowedSubjects={allowedSubjects}
                    onNewGraduationYearSelected={(gradYear) => this.onGraduationYearSelected(gradYear)}
                    onNewSchoolSelected={(schoolSel) => this.onNewSchoolSelected(schoolSel)}
                    onNewPeriodSelected={(period) => this.onNewPeriodSelected(period)}
                    onNewTimeRangeSelected={(timeRange) => this.onTimeRangeSelected(timeRange)}
                    onNewSubjectSelected={(subject) => this.onSubjectSelected(subject)}
                />

                {!loaded && <ReportLoadingMessage />}

                <hr />

                {periodSubviewActive ? 
                    <BellringersPeriodSubview grades={grades} info={subviewInfo} onQuestionClick={this.handleQuestionClick}/> 
                    : 
                    <BellringersSubjectSubview grades={grades} info={subviewInfo} onQuestionClick={this.handleQuestionClick}/>
                }

                <BellringersModal 
                    show={showModalForBellringerQuestionReview}
                    onClose={this.handleReviewClose}
                    dismissible={true}>

                    <BellringerQuestionReviewForTeacher bellringerId={selectedBellringerId!} info={teacherReviewInfo} onClose={this.handleReviewClose}/>

                </BellringersModal>
            </div>
        )
    }
}
